import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type RefObject,
} from "react";
import photoFactory from "../../data/unsplash-images";
import "./pulse-background.scss";

const DEFAULT_LOOP_INTERVAL_MS = 3000;
const IMAGE_SUFFIX = "?w=1920&auto=format&fit=crop&q=80";

export type PulseBackgroundMode = "loop" | "once";
export type PulseBackgroundPlacement = "fullscreen" | "anchor";

// Tipagem alinhada com as opções da sua Factory
export type PhotoSelectionType = "nav" | "custom";

export interface PulseBackgroundWaveProps {
  placement?: PulseBackgroundPlacement;
  anchorRef?: RefObject<HTMLElement | null>;
  mode?: PulseBackgroundMode;
  removeOnComplete?: boolean;
  onComplete?: () => void;
  zIndex?: number | string;
  pulseIntervalMs?: number;
  className?: string;
  photoSelectionType?: PhotoSelectionType;
  photoCustomIndexes?: number[];
  origin?: { x: string; y: string };
  durationMs?: number;
}

function formatUrl(base: string) {
  return `${base}${IMAGE_SUFFIX}`;
}

function pickRandomImage(
  selectionType?: PhotoSelectionType,
  excludeBase?: string,
  indexes?: number[],
) {
  // Invoca a factory passando o objeto de configuração correto
  const photos = photoFactory({ type: selectionType, indexes });

  if (!photos || photos.length === 0) {
    return "";
  }

  const pool = excludeBase
    ? photos.filter((url) => url !== excludeBase)
    : photos;

  const finalPool = pool.length > 0 ? pool : photos;

  return finalPool[Math.floor(Math.random() * finalPool.length)];
}

function preloadImage(url: string) {
  return new Promise<void>((resolve, reject) => {
    if (!url) return reject(new Error("Empty URL"));
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => reject(new Error(`Failed to load ${url}`));
    img.src = url;
  });
}

function sleep(ms: number) {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function waitForPaint() {
  return new Promise<void>((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

function useAnchorCenter(anchorRef?: RefObject<HTMLElement | null>) {
  const [center, setCenter] = useState<{ x: number; y: number } | null>(null);

  useLayoutEffect(() => {
    const el = anchorRef?.current;
    if (!el) {
      setCenter(null);
      return;
    }

    const update = () => {
      const rect = el.getBoundingClientRect();
      setCenter({
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      });
    };

    update();
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(el);
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [anchorRef]);

  return center;
}

export function PulseBackgroundWave({
  placement = "fullscreen",
  anchorRef,
  mode = "loop",
  removeOnComplete = false,
  onComplete,
  zIndex,
  pulseIntervalMs,
  className,
  photoSelectionType,
  photoCustomIndexes,
  origin,
  durationMs,
}: PulseBackgroundWaveProps) {
  const revealRef = useRef<HTMLDivElement>(null);
  const runningRef = useRef(true);
  const onCompleteRef = useRef(onComplete);
  const anchorCenter = useAnchorCenter(
    placement === "anchor" ? anchorRef : undefined,
  );

  const intervalMs =
    pulseIntervalMs ?? (mode === "loop" ? DEFAULT_LOOP_INTERVAL_MS : 0);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    runningRef.current = true;

    const pulse = () =>
      new Promise<void>((resolve) => {
        const el = revealRef.current;
        if (!el) {
          resolve();
          return;
        }

        const onEnd = (event: AnimationEvent) => {
          if (event.animationName !== "pulse-bg-reveal") return;
          el.removeEventListener("animationend", onEnd);
          el.classList.remove("is-pulsing");
          resolve();
        };

        el.classList.remove("is-pulsing");
        void el.offsetWidth;
        el.classList.add("is-pulsing");
        el.addEventListener("animationend", onEnd);
      });

    const setBackground = (url: string) => {
      const el = revealRef.current;
      if (el) {
        el.style.backgroundImage = `url("${url}")`;
      }
    };

    const loadWithFallback = async (base: string) => {
      const primary = formatUrl(base);
      try {
        await preloadImage(primary);
        return { base, url: primary };
      } catch {
        const fallbackBase = pickRandomImage(
          photoSelectionType,
          base,
          photoCustomIndexes,
        );
        const fallbackUrl = formatUrl(fallbackBase);
        await preloadImage(fallbackUrl);
        return { base: fallbackBase, url: fallbackUrl };
      }
    };

    const finish = () => {
      onCompleteRef.current?.();
    };

    (async () => {
      const firstImage = pickRandomImage(
        photoSelectionType,
        undefined,
        photoCustomIndexes,
      );
      if (!firstImage) return;

      let current = await loadWithFallback(firstImage);
      if (!runningRef.current) return;

      await sleep(intervalMs);
      if (!runningRef.current) return;

      if (mode === "once") {
        setBackground(current.url);
        await waitForPaint();
        await pulse();
        if (!runningRef.current) return;
        finish();
        return;
      }

      while (runningRef.current) {
        setBackground(current.url);
        await waitForPaint();
        await pulse();
        if (!runningRef.current) break;

        const nextBase = pickRandomImage(
          photoSelectionType,
          current.base,
          photoCustomIndexes,
        );
        const nextLoad = loadWithFallback(nextBase);

        await sleep(intervalMs);
        if (!runningRef.current) break;

        current = await nextLoad;
      }
    })();

    return () => {
      runningRef.current = false;
    };
  }, [mode, intervalMs, photoSelectionType, photoCustomIndexes]);

  const containerClassName = ["pulse-background", className]
    .filter(Boolean)
    .join(" ");

  const containerStyle: CSSProperties | undefined =
    zIndex !== undefined
      ? { zIndex }
      : placement === "anchor"
        ? { zIndex: "calc(var(--z-top) - 1)" }
        : undefined;

  const revealStyle = {
    ...(placement === "anchor" && anchorCenter
      ? {
          "--pulse-origin-x": `${anchorCenter.x}px`,
          "--pulse-origin-y": `${anchorCenter.y}px`,
        }
      : origin
        ? {
            "--pulse-origin-x": origin.x,
            "--pulse-origin-y": origin.y,
          }
        : undefined),
    "--pulse-duration": durationMs ? `${durationMs}ms` : undefined,
  } as CSSProperties;

  return (
    <div className={containerClassName} style={containerStyle} aria-hidden>
      <div
        ref={revealRef}
        className="pulse-background__reveal"
        style={revealStyle}
      />
    </div>
  );
}
